/**
 * Flujo idéntico a appweb_ci4/app/Views/ranking/character-profile.php
 * CI4: <script itemTooltip.js> → renderCharacterProfile(charId, el)
 * React/WebView: + precache axios (API en otro dominio) + auth en apiFetch
 */
import { api, installWebViewAuthBridge } from '@/lib/axios';
import {
  ensureItemTooltipReady,
  loadItemTooltipScript,
  patchItemTooltipApiFetch,
} from '@/lib/itemTooltipBridge';
import { applyCharacterSidePreload, type PreloadedSideData } from './characterProfileSide';

export { loadItemTooltipScript, patchItemTooltipApiFetch, ensureItemTooltipReady };

/** CI4 same-origin no lo necesita; WebView CDN → API host requiere precache. */
async function seedCachesFromApi(charId: number, side: PreloadedSideData | null): Promise<void> {
  const tt = window.ItemTooltip;
  if (!tt) return;

  tt.baseUrl = window.__APPWEB_API_BASE__ || '/api/v1';

  if (side?.buffs) {
    tt.cache.set(`buffs_${charId}`, side.buffs);
  }

  try {
    const [profileRes, avatarRes] = await Promise.all([
      api.get<{ success: boolean }>(`/api/v1/character/${charId}/profile`),
      api.get<{ success: boolean }>(`/api/v1/character/${charId}/avatar`),
    ]);
    if (profileRes.data?.success) {
      tt.cache.set(`profile_${charId}`, profileRes.data);
    }
    if (avatarRes.data?.success) {
      tt.cache.set(`avatar_${charId}`, avatarRes.data);
    }
  } catch {
    /* itemTooltip hará apiFetch como fallback (CI4 puro) */
  }
}

/**
 * Equivalente a initCharacterProfile() en character-profile.php de CI4.
 */
export async function initCharacterProfileLikeCI4(
  charId: number,
  container: HTMLElement,
  side: PreloadedSideData | null,
): Promise<void> {
  installWebViewAuthBridge();
  await ensureItemTooltipReady();

  if (side) {
    applyCharacterSidePreload(charId, side);
  }

  await seedCachesFromApi(charId, side);

  const render = window.ItemTooltip?.renderCharacterProfile;
  if (!render) {
    throw new Error('itemTooltip-not-available');
  }

  await render.call(window.ItemTooltip, charId, container);
}
